<!-- Form Container -->
<div
  data-testid="user-settings-page"
  class="h-[calc(100vh-60px)] w-full max-w-full flex flex-col overflow-x-hidden"
>
  <!-- Header with action buttons -->
  <div
    class="flex flex-col sm:flex-row flex-wrap justify-between items-start sm:items-center gap-3 sticky top-0 bg-white z-10 px-4 sm:px-6 pt-4 pb-4 border-b border-gray-100 w-full max-w-[848px] mx-auto"
  >
    <h2 class="text-xl sm:text-2xl font-semibold text-gray-800">
      {{ "user_settings.title" | translate }}
    </h2>
    <div class="flex flex-wrap items-center gap-2 sm:gap-3 w-full sm:w-auto">
      <app-button
        [testId]="'user-settings-save-btn'"
        [label]="'user_settings.actions.save_changes'"
        icon="check"
        (emitOnClick)="onSaveChanges()"
        [disabled]="!hasChanges()"
        type="primary"
        class="text-sm"
      ></app-button>
      <app-button
        [testId]="'user-settings-cancel-btn'"
        [label]="'user_settings.actions.cancel'"
        icon="close"
        (emitOnClick)="onCancel()"
        [outlined]="true"
        type="secondary"
        class="text-sm"
      ></app-button>
    </div>
  </div>

  <!-- Scrollable content area -->
  <div class="flex-1 min-h-0 overflow-auto px-4 sm:px-6">
    <div class="p-4 sm:p-6">
      <!-- Language Settings Section -->
      <section class="form-section mb-6 sm:mb-8 max-w-[800px] mx-auto">
        <h2
          class="section-title text-base sm:text-lg font-semibold mb-3 sm:mb-4 px-4 sm:px-0"
        >
          {{ "user_settings.sections.language" | translate }}
        </h2>
        <div class="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200">
          <!-- Mobile: Stack vertically, Desktop: horizontal layout -->
          <div
            class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 sm:gap-0"
          >
            <label
              for="us-language"
              class="form-label text-base font-medium text-center sm:text-left w-full sm:w-auto"
            >
              {{ "user_settings.descriptions.language" | translate }}
            </label>
            <p-select
              data-testid="user-settings-language-select"
              [(ngModel)]="selectedLanguageCode"
              [options]="availableLanguages"
              [placeholder]="
                'user_settings.placeholders.select_language' | translate
              "
              optionLabel="name"
              optionValue="code"
              inputId="us-language"
              class="w-full sm:w-48 text-base"
              (onChange)="onLanguageChange($event)"
              [panelStyleClass]="'mobile-select-panel'"
            ></p-select>
          </div>
        </div>
      </section>

      <!-- Menu Display Section -->
      <section class="form-section mb-6 sm:mb-8 max-w-[800px] mx-auto">
        <h2
          class="section-title text-base sm:text-lg font-semibold mb-3 sm:mb-4 px-4 sm:px-0"
        >
          {{ "user_settings.sections.menu_display" | translate }}
        </h2>
        <div class="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200">
          <div
            class="flex flex-row justify-between items-center gap-4"
          >
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <label
                for="us-show-unavailable"
                class="form-label text-base font-medium text-left"
              >
                {{
                  "user_settings.descriptions.show_unavailable_menu_items"
                    | translate
                }}
              </label>
              <span class="text-sm text-gray-500 text-left">
                {{
                  "user_settings.hints.show_unavailable_menu_items" | translate
                }}
              </span>
            </div>
            <p-toggleswitch
              data-testid="user-settings-show-unavailable-toggle"
              inputId="us-show-unavailable"
              class="shrink-0"
              [ngModel]="showUnavailableMenuItems()"
              (ngModelChange)="onShowUnavailableMenuItemsChange($event)"
            />
          </div>
        </div>
      </section>

      <!-- Multi-Tenancy Display Section (Admin / On-Premise Deployment Control) -->
      <section
        data-testid="user-settings-tenant-section"
        class="form-section mb-6 sm:mb-8 max-w-[800px] mx-auto"
      >
        <h2
          class="section-title text-base sm:text-lg font-semibold mb-3 sm:mb-4 px-4 sm:px-0"
        >
          {{ "user_settings.sections.multi_tenancy" | translate }}
        </h2>
        <div class="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 space-y-5">
          <div
            class="flex flex-row justify-between items-center gap-4"
          >
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <label
                for="us-show-tenant-switcher"
                class="form-label text-base font-medium text-left"
              >
                {{
                  "user_settings.descriptions.show_tenant_switcher"
                    | translate
                }}
              </label>
              <span class="text-sm text-gray-500 text-left">
                {{
                  "user_settings.hints.show_tenant_switcher" | translate
                }}
              </span>
            </div>
            <p-toggleswitch
              data-testid="user-settings-show-tenant-switcher-toggle"
              inputId="us-show-tenant-switcher"
              class="shrink-0"
              [ngModel]="showTenantSwitcher()"
              (ngModelChange)="onShowTenantSwitcherChange($event)"
            />
          </div>

          <!-- Multi-Tenancy CRUD & Package Administration Controls -->
          <div
            data-testid="user-settings-tenant-crud-controls"
            class="border-t border-gray-200 pt-4 flex flex-col justify-between items-start gap-3"
          >
            <div class="flex flex-col gap-1">
              <span class="form-label text-base font-medium text-gray-800">
                {{ "user_settings.descriptions.tenant_crud_management" | translate }}
              </span>
              <span class="text-sm text-gray-500">
                {{ "user_settings.hints.tenant_crud_management" | translate }}
              </span>
            </div>
            <div class="flex flex-wrap items-center gap-2.5 w-full">
              <app-button
                [testId]="'user-settings-manage-tenants-btn'"
                [label]="'user_settings.actions.manage_tenants'"
                icon="domain"
                (emitOnClick)="openTenantManagement()"
                type="primary"
                class="text-sm"
              ></app-button>
              <app-button
                [testId]="'user-settings-manage-tenant-packages-btn'"
                [label]="'user_settings.actions.manage_tenant_packages'"
                icon="inventory_2"
                (emitOnClick)="openTenantPackages()"
                [outlined]="true"
                type="secondary"
                class="text-sm"
              ></app-button>
              <app-button
                [testId]="'user-settings-manage-platform-modules-btn'"
                [label]="'user_settings.actions.manage_platform_modules'"
                icon="tune"
                (emitOnClick)="openPlatformModules()"
                [outlined]="true"
                type="secondary"
                class="text-sm"
              ></app-button>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</div>

<!-- Toast for messages -->
<p-toast data-testid="user-settings-toast"></p-toast>
